/**
 * src/lib/coverLetterPolling.js — RC 1.52 Phase 2 cover-letter polling helper.
 *
 * The cover-letter generation route now returns one of three shapes
 * depending on the kill-switch + dispatch outcome:
 *
 *   • Flag OFF (legacy inline path): sync 200 with the full cover-
 *     letter envelope. No polling required.
 *   • Flag ON, fast path (≤25 s LLM): sync 200 with `served_via:
 *     'sync_200'` and the full envelope. No polling required.
 *   • Flag ON, replay match: sync 200 with `served_via:
 *     'replay_200'`, `idempotent_replay:true`. No polling required.
 *   • Flag ON, slow path: HTTP 202 with `served_via:'async_202'`,
 *     `run_id`, `poll_url`. Frontend polls
 *     `GET /api/cover-letters/{run_id}` at the server-suggested
 *     cadence (`retry_after_ms`) until the row reaches a terminal
 *     `run_status` (`complete` | `failed`).
 *
 * The helper accepts a dispatch function (`postFn`) instead of
 * embedding `apiFetch` so unit tests can substitute a mock easily.
 *
 * Operator invariants honoured:
 *   • Invariant P1 — `poll_url` always resolves.
 *   • Invariant P2 — terminal-complete poll surfaces the FULL
 *     canonical result block (no sparse envelope).
 *   • Invariant P4 — honour `retry_after_ms` when present.
 *   • Invariant R1 — recovery is id-driven (run_id), not title-stamp.
 *   • Invariant R2 — terminal hydration returns the SAME shape as
 *     sync 200 so the CoverLetterSection's success path is unchanged.
 *   • Invariant R4 — `visibilitychange` triggers an immediate
 *     re-poll if the next scheduled tick is > 2 s away.
 *   • Invariant R5 — backgrounded tab does not lose recovery; the
 *     run_id is the recovery handle (no marker dependency).
 *
 * Hard rails:
 *   • The helper makes ONE LLM-dispatching POST. All subsequent
 *     reads are free polls.
 *   • Idempotency_key is passed through verbatim from the caller —
 *     repeat dispatches under the same key short-circuit to
 *     replay_200 on the backend, never re-charging.
 */

import { BUNDLE_VERSION } from './buildMeta';
import { playCompletionChime } from './completionChime';

// RC 1.55 Phase Z.4 (operator-locked, 2026-05-13 PM) — dispatch
// surface identifier. See cvVariantPolling.js for the strict-hold
// rationale. Hard-coded per file so a copy-paste into a new
// surface trips a code-review signal.
const _DISPATCH_SURFACE = 'workspace_cover_letter_section_v1';

const DEFAULT_POLL_CADENCE_MS    = 3000;
const POLLING_HARD_CAP_MS        = 120_000;   // 2 min ceiling; matches operator-locked window
const MIN_POLL_CADENCE_MS        = 1500;      // floor — server hint never overrides this
const VISIBILITY_FAST_POLL_MS    = 500;       // when tab returns, poll within 0.5 s

/**
 * Polls `GET /api/cover-letters/{run_id}` until terminal state.
 *
 * @param {string} runId            UUID of the pending run row.
 * @param {object} deps
 * @param {(path:string)=>Promise<object>} deps.apiGet  GET helper.
 * @param {AbortSignal} [deps.signal]                   Abort handle.
 * @param {(state:string)=>void} [deps.onRunStatus]     Lifecycle hook
 *                                                       ("running"|"complete"|"failed").
 * @returns {Promise<object>}                            Terminal envelope.
 * @throws  {Error} on abort, failed run, or hard-cap timeout.
 */
export async function pollCoverLetterRun(runId, deps) {
  const { apiGet, signal, onRunStatus } = deps;
  if (!runId) throw new Error('pollCoverLetterRun: runId is required');

  const deadline = Date.now() + POLLING_HARD_CAP_MS;
  let cadence = DEFAULT_POLL_CADENCE_MS;

  // R4 — when the tab becomes visible, schedule the next poll within
  // VISIBILITY_FAST_POLL_MS so the user sees fresh state on return.
  let visibilityNudge = null;
  const onVisible = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      if (visibilityNudge) clearTimeout(visibilityNudge);
      // The actual poll loop reads `nextWakeMs` each iteration; we
      // shorten it here so the next iteration fires fast.
      cadence = VISIBILITY_FAST_POLL_MS;
    }
  };
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', onVisible);
  }

  try {
    while (Date.now() < deadline) {
      if (signal && signal.aborted) {
        throw new Error('Polling aborted');
      }
      let row = null;
      try {
        // RC 1.52 Phase 2 Hotfix — Invariant P2 (header-gated
        // canonical envelope). The backend's terminal-complete
        // GET returns the raw row for legacy callers; this header
        // opts the polling lane into the canonical envelope shape
        // so `billing` / `charge_reason` / `served_via` survive.
        row = await apiGet(`/api/cover-letters/${runId}`, {
          headers: { 'X-RC-1-52-Poll': '1' },
        });
      } catch (err) {
        // Transient read error — retry on the next cadence tick.
        // Network blips on mobile during long LLM dispatches are
        // expected; the durable row is the source of truth.
        // eslint-disable-next-line no-console
        console.warn('[RC 1.52 coverLetterPolling] transient read error', err);
        row = null;
      }

      if (row) {
        // Distinguish polling envelope (queued/running/failed) from
        // a terminal-complete row passthrough (back-compat shape:
        // raw `application_documents` row).
        const status = (row.status || row.run_status || 'complete').toLowerCase();
        if (typeof onRunStatus === 'function') {
          try { onRunStatus(status); } catch { /* noop */ }
        }

        if (status === 'complete') {
          // Long-running completion notification only.
          try { playCompletionChime(); } catch (_) { /* silent */ }
          // Three shapes possible:
          //  - canonical poll envelope (RC 1.52 P2 — header-gated):
          //      { status:'complete', result:{...}, billing, charge_reason,
          //        served_via, run_id, idempotent_replay, ... }
          //  - direct row passthrough (legacy / pre-Phase-2 rows
          //    OR no-header back-compat caller)
          if (row.result && typeof row.result === 'object') {
            // R2 — flatten to match sync_200 envelope shape so the
            // success path reads `r.billing` / `r.charge_reason` /
            // `r.served_via` verbatim (no result-block awareness).
            return {
              ...row.result,
              billing:           row.billing ?? null,
              charge_reason:     row.charge_reason ?? null,
              served_via:        row.served_via ?? 'poll_200',
              run_id:            row.run_id ?? row.result.cover_letter_id,
              status:            'complete',
              idempotent_replay: row.idempotent_replay ?? false,
            };
          }
          // Direct row passthrough — return the canonical fields the
          // CoverLetterSection success path consumes.
          return _rowToCanonical(row);
        }

        if (status === 'failed') {
          const err = new Error(
            row.error_detail || row.run_error_detail
              || 'Cover letter generation failed.',
          );
          err.code = row.error_code || row.run_error_code || 'cover_letter_failed';
          err.runStatus = 'failed';
          err.runId = runId;
          throw err;
        }

        // Honour the server's retry_after_ms hint (P4), floored at
        // MIN_POLL_CADENCE_MS so the loop can't burn the worker.
        if (typeof row.retry_after_ms === 'number'
            && row.retry_after_ms >= MIN_POLL_CADENCE_MS) {
          cadence = row.retry_after_ms;
        }
      }

      // eslint-disable-next-line no-await-in-loop
      await _sleep(cadence, signal);
    }
    // RC 1.52 Phase 2 Hotfix D-3 — defensive recoverable error.
    // After D-1 + D-2 ship, the backend reaches a terminal state
    // (complete OR failed) within FAST_PATH_SECONDS + PER_CALL_TIMEOUT
    // ≈ 115 s, comfortably inside the 120 s POLLING_HARD_CAP_MS. So
    // this branch should NEVER fire in normal operation. If it does,
    // it indicates a backend regression — we surface a calm,
    // recoverable signal so the section can show a "still running,
    // check Documents" toast rather than a hard error/idle reset.
    // The handler MUST NOT use this branch to mask backend bugs;
    // any occurrence is itself a bug worth investigating.
    const err = new Error('Cover letter still generating in the background.');
    err.code = 'cover_letter_still_running';
    err.runId = runId;
    err.recoverable = true;
    throw err;
  } finally {
    if (typeof document !== 'undefined' && document.removeEventListener) {
      document.removeEventListener('visibilitychange', onVisible);
    }
    if (visibilityNudge) clearTimeout(visibilityNudge);
  }
}

/**
 * Submit a cover-letter generation request and resolve to the
 * canonical sync-200 envelope, regardless of whether the backend
 * returned a fast-path result or routed through async polling.
 *
 * Mirrors `analyseFitPolling::runAnalyzeFitWithPolling`.
 *
 * @param {object}   args
 * @param {object}   args.requestBody — POST body for /api/runs.
 * @param {(path:string, opts:object)=>Promise<object>} args.apiPost
 * @param {(path:string)=>Promise<object>}              args.apiGet
 * @param {AbortSignal}                                 [args.signal]
 * @param {(state:string)=>void}                        [args.onRunStatus]
 * @returns {Promise<object>} canonical cover-letter envelope.
 */
export async function generateCoverLetterWithPolling(args) {
  const { requestBody, apiPost, apiGet, signal, onRunStatus, telemetryHeaders, onRunIdReady } = args;
  if (!apiPost || !apiGet) {
    throw new Error('generateCoverLetterWithPolling: apiPost + apiGet required');
  }

  // RC 1.55 Phase Z.4 — read-only forensic telemetry. See
  // cvVariantPolling.js for the strict-hold rationale.
  const _telemetryHeaders = {
    'X-Ndor-Dispatch-Surface': _DISPATCH_SURFACE,
    'X-Ndor-Bundle-Version':   BUNDLE_VERSION,
    ...(telemetryHeaders || {}),
  };

  const envelope = await apiPost('/api/runs', {
    method:  'POST',
    headers: _telemetryHeaders,
    body:    { action_code: 'cover_letter', payload: requestBody },
  });

  if (!envelope || typeof envelope !== 'object') {
    throw new Error('cover-letter run dispatch returned non-object');
  }

  const served = envelope.served_via || 'sync_200';

  // Fast path / replay — payload IS the canonical result shape.
  if (served === 'sync_200' || served === 'replay_200') {
    if (envelope.result && typeof envelope.result === 'object') {
      return envelope.result;
    }
    return envelope;
  }

  // async_202 — poll until terminal.
  if (served === 'async_202') {
    const runId = envelope.run_id;
    if (!runId) {
      throw new Error('cover-letter async_202 envelope missing run_id');
    }
    // CWS-2 (operator-locked, 2026-02-15) — surface the runId to the
    // caller BEFORE polling starts. Section uses this to stamp the
    // inflight marker so a tab/section switch mid-poll can resume
    // via `pollCoverLetterRun(runId)` on remount.
    if (typeof onRunIdReady === 'function') {
      try { onRunIdReady(runId); } catch { /* noop */ }
    }
    if (typeof onRunStatus === 'function') {
      try { onRunStatus('running'); } catch { /* noop */ }
    }
    return pollCoverLetterRun(runId, { apiGet, signal, onRunStatus });
  }

  // Unknown served_via — defensive passthrough.
  return envelope.result || envelope;
}

// ─────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────

function _sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal && signal.addEventListener) {
      signal.addEventListener('abort', () => {
        clearTimeout(t);
        reject(new Error('Polling aborted'));
      });
    }
  });
}

/**
 * Map a raw `application_documents` row to the canonical cover-
 * letter envelope. Used when the polling endpoint returns the
 * back-compat shape (legacy `run_status='complete'` rows pre-
 * dating the Phase 2 polling-envelope branch).
 */
function _rowToCanonical(row) {
  const cj = row.content_json || {};
  return {
    cover_letter_id:      row.id,
    title:                row.title,
    tone_type:            row.tone_type,
    fit_score:            cj.fit_score,
    generated_text:       row.content_markdown || '',
    linked_cv_variant_id: row.cv_variant_id,
    fabrication_check:    cj.fabrication_check || { ok: true },
    created_at:           row.created_at,
  };
}
